import { groupBy, values } from 'lodash';
import { min, select, transition } from 'd3';
import { hexbin as d3Hexbin, HexbinBin } from 'd3-hexbin';

import * as K from 'common/constants';
import history from 'service/customHistory';
import { AvailableTheme, HoneyCombChartDataType } from 'common/types';
import { DashboardHoneyCombColorType } from 'components/dashboards/types';
import { THEMED_COMPONENT, themes } from 'styles/stylesProvider/themes';

const MAX_HEX_RADIUS = 15;

function getPlaceHolderElems(rows: number, columns: number, len: number, radius: number) {
  let points = [];
  for (let i = 0, count = 0; i < rows; i++) {
    for (let j = 0; j < columns && count <= len - 1; j++, count++) {
      points.push([radius * j * 1.75, radius * i * 1.5]);
    }
  }
  return points;
}
// function to replace variables added between square brackets '[]' in the linkTo field with the cell values
const replaceLink = (linkTo: string, data: any) =>
  linkTo.replace(/\[[^[\]]*\]/g, (v) => {
    // remove square brackets (first and last character) from identified variable match
    const key = v.slice(1, -1);
    // return cell values from the identified key
    return key in data ? data[key] : '';
  });

function bestFitElemCountPerRow(bucketLen: number, width: number, height: number) {
  let countPerRow = bucketLen;
  for (let r = 1, base = Infinity; r <= bucketLen; r += 1) {
    const w = Math.floor(width / r);
    const h = (2 * w) / 3;
    const netSpaceLost = Math.abs(width * height - bucketLen * w * h);
    if (netSpaceLost < base) {
      base = netSpaceLost;
      countPerRow = r;
    }
  }
  return countPerRow;
}

function getColorValue(val: number, thresholds: string[]) {
  if (val < parseInt(thresholds[1]) || isNaN(val)) {
    return K.HONEYCOMB_THRESHOLD_OPACITY[0];
  } else if (val < parseInt(thresholds[2])) {
    return K.HONEYCOMB_THRESHOLD_OPACITY[1];
  } else if (val < parseInt(thresholds[3])) {
    return K.HONEYCOMB_THRESHOLD_OPACITY[2];
  } else {
    return K.HONEYCOMB_THRESHOLD_OPACITY[3];
  }
}

// Define the div for the tooltip
const tooltip = select('body').append('div').attr('class', 'DasbhoardHoneyComb__Tooltip').style('display', 'none');

type renderProps = {
  width: number;
  height: number;
  x: number;
  y: number;
};

// TBD remove the any type below
function renderHoneyComb(
  data: HoneyCombChartDataType[],
  props: renderProps,
  groupEl: any,
  columns: string[],
  thresholds: string[],
  color: DashboardHoneyCombColorType,
  theme: AvailableTheme,
  honeyCombLinkTo: string,
) {
  const { width, height, x, y } = props;
  // by default considering the last index value as the numeric item,
  function getColors(d: any, i: number) {
    var value = data[i][columns[columns.length - 1]];
    return getColorValue(value as number, thresholds);
  }
  const svgGroup = select(groupEl.current);
  const t = transition().duration(750);
  const tooltipColor = (themes[theme][THEMED_COMPONENT.HIGHCHARTS] as any).tooltip;
  const tooltipBackground = (themes[theme][THEMED_COMPONENT.HIGHCHARTS] as any).tooltipBackground;
  const MapColumns = Math.ceil(data.length / Math.floor(Math.sqrt(data.length)));
  const MapRows = Math.ceil(data.length / MapColumns);

  // The maximum radius the hexagons can have to still fit the screen
  let hexRadius = Math.floor(
    min([width / ((MapColumns + 0.5) * Math.sqrt(3)), height / ((MapRows + 1 / 3) * 1.5), width / 7]) as number,
  );
  // limit hexagon radius to 15
  if (hexRadius > MAX_HEX_RADIUS) {
    hexRadius = MAX_HEX_RADIUS;
  }

  var points = getPlaceHolderElems(MapRows, MapColumns, data.length, hexRadius);
  // calculate x and y points for the hexagon based on rad
  var offSetX = hexRadius * Math.cos((30 * Math.PI) / 180);
  var adjustedOffSetX = offSetX + (width - offSetX * 2 * MapColumns - offSetX) / 2;
  var offSetY = hexRadius;

  //Set the hexagon radius
  const hexbin = d3Hexbin().radius(hexRadius);
  const zeroHexBin = d3Hexbin().radius(0);

  const translateX = adjustedOffSetX + x;
  const translateY = offSetY + y;
  // adding 12 to the height to cover panel title
  svgGroup
    .attr('width', width)
    .attr('height', height)
    .text('axis')
    .attr('transform', `translate(${translateX},${translateY})`);
  //Draw the hexagons
  var hexagons = svgGroup.selectAll('.hexagon').data(hexbin(points as [number, number][]));
  hexagons.exit().remove();

  hexagons
    .transition(t as unknown as string)
    .style('fill', color.value)
    .style('opacity', getColors)
    .attr('d', function (d) {
      return 'M' + d.x + ',' + d.y + hexbin.hexagon();
    })
    .attr('padding', '2px');

  hexagons
    .enter()
    .append('path')
    .attr('class', 'hexagon')
    .attr('d', function (d) {
      return 'M' + d.x + ',' + d.y + zeroHexBin.hexagon();
    })
    .on('mouseover', function (this, e) {
      const p = svgGroup.selectAll('.hexagon');
      const i = p.nodes().indexOf(this);
      const currentData: { [key: string]: any } = data[i];

      const tableContent = columns
        .map((col) =>
          col !== 'Cluster'
            ? `<tr>
            <td>${col}:</td>
            <td><b>${isNaN(currentData[col]) ? currentData[col] : currentData[col].toFixed(2)}</b></td>
          </tr>`
            : '',
        )
        .join('');
      const content = `
        <p><b>${currentData.Cluster}</b></p>
        <table>
          ${tableContent}
        </table>
      `;

      tooltip.transition().duration(200).style('opacity', 0.9);
      tooltip.style('visibility', 'visible');
      select(this).style('fill', color.hover);
      select(this).style('fill-opacity', 1);
      select(this).style('cursor', 'pointer');
      // offset 10 and 35 are used to adjust the tooltip position relative to the current hover position
      // TBD colors are hardcoded temporarily, will remove in the follow up PR
      tooltip
        .html(content)
        .style('background-color', tooltipBackground)
        .style('color', tooltipColor)
        .style('opacity', 0)
        .style('left', e.pageX + 10 + 'px')
        .style('top', e.pageY - 35 + 'px')
        .style('display', 'block')
        .style('box-shadow', '0px 0 16px rgb(0 0 0 / 40%)');
    })
    .on('mouseout', function (this, e, d: HexbinBin<[number, number]>) {
      const p = svgGroup.selectAll('.hexagon');
      const i = p.nodes().indexOf(this);
      tooltip.transition().duration(500).style('opacity', 0);
      select(this).transition().style('opacity', 1);
      select(this).style('fill', color.value);
      select(this).style('fill-opacity', getColors(d, i));
      select(this).style('cursor', 'default');
    })
    .on('click', function (this) {
      if (honeyCombLinkTo) {
        const p = svgGroup.selectAll('.hexagon');
        const i = p.nodes().indexOf(this);
        const currentData: { [key: string]: any } = data[i];
        const url = replaceLink(honeyCombLinkTo, currentData);
        if (url.includes('http')) {
          window.location.href = url;
        } else {
          history.push(url);
        }
        tooltip.transition().duration(500).style('opacity', 0);
      }
    })
    .attr('stroke', theme === AvailableTheme.SENTINEL_ONE_DARK ? '#282e36' : '#FFFFFF')
    .attr('stroke-width', '4px')
    .style('fill', color.value)
    .style('fill-opacity', getColors)
    .transition(t as unknown as string)
    .attr('d', function (d) {
      return 'M' + d.x + ',' + d.y + hexbin.hexagon();
    });
}
// TBD remove the any type below
export function renderFn(
  buckets: any,
  { width, height, parentGroupEl }: any,
  groupEl: any,
  columns: string[],
  thresholds: string[],
  color: DashboardHoneyCombColorType,
  theme: AvailableTheme,
  honeyCombLinkTo: string,
) {
  // Adding data to the svg-group elements
  const parentGroup = select(parentGroupEl)
    .attr('width', width)
    .attr('height', height - 30);

  const groups = parentGroup.selectAll('g').data(buckets);

  // Determine the best fitting countPerRow. (Min. unused space).
  let countPerRow = bestFitElemCountPerRow(buckets.length, width, height);
  // Deterimine the unit dimensions for each svg group.
  const unitWidth = Math.floor(width / countPerRow);
  const rowCount = Math.ceil(buckets.length / countPerRow);
  const unitHeight = height / rowCount;

  function childRenderFn(d: any, i: number) {
    const x = unitWidth * (i % countPerRow);
    const y = unitHeight * Math.floor(i / countPerRow);
    // Send the unit diminesions to update the groups dimensions and (x, y) for appropiate translations
    renderHoneyComb(
      d,
      {
        width: unitWidth,
        height: unitHeight,
        x,
        y,
      },
      groupEl,
      columns,
      thresholds,
      color,
      theme,
      honeyCombLinkTo,
    );
  }
  groups.each(childRenderFn);

  groups.exit().remove();

  groups.enter().append('g').each(childRenderFn);
}

export const bucketizeData = (visualData: any[], groupByColumn: string, accum: any[] = []) => {
  const groupedValues = groupBy(visualData, (data) => data[groupByColumn]);
  const selectedValues = values(groupedValues);
  accum.push(...selectedValues);
  return accum;
};
